/**
 * Pure-TS write-safety hold for the ctx-param gate (#404, slice 1c).
 *
 * Subscribes to the TanStack mutation cache and the upload-queue Zustand store;
 * resolves exactly once when both are clear, or after a timeout ceiling.
 * No React hooks — lifetime is managed by the caller via `cancel()`.
 */

import type { QueryClient } from '@tanstack/react-query';
import { useUploadQueueStore } from '@/modules/upload-queue/store/useUploadQueueStore';
import { ACTIVE_STATUSES } from '@/modules/upload-queue/constants';
import { STREAM_MARK_READ_MUTATION_KEY } from '@/api/stream';

export type HoldResult = 'settled' | 'timeout';

export interface HoldOptions {
  queryClient: QueryClient;
  maxMs: number;
  onTimeout?: () => void;
}

export interface HoldHandle {
  promise: Promise<HoldResult>;
  cancel: () => void;
}

/**
 * In-flight mutations the hold waits for: every save except a notification's
 * mark-read, which navigation never waits on (Q-506 card 1571).
 */
export function countBlockingMutations(queryClient: QueryClient): number {
  return queryClient.isMutating({
    predicate: (m) => m.options.mutationKey?.join('/') !== STREAM_MARK_READ_MUTATION_KEY.join('/'),
  });
}

function hasActiveUploads(): boolean {
  return useUploadQueueStore.getState().items.some(
    (item) => (ACTIVE_STATUSES as readonly string[]).includes(item.status),
  );
}

/**
 * Wait until all in-flight mutations and active uploads have settled,
 * or until `maxMs` elapses. Returns a handle with a promise that resolves
 * exactly once and a `cancel()` that tears down all listeners immediately.
 *
 * - `'settled'`: mutations and uploads cleared before the timeout.
 * - `'timeout'`: the ceiling elapsed first; `onTimeout` was called.
 * - If `cancel()` is called, the promise never resolves (listeners removed).
 */
export function holdUntilWritesSettle(opts: HoldOptions): HoldHandle {
  const { queryClient, maxMs, onTimeout } = opts;

  let resolve: ((result: HoldResult) => void) | null = null;
  let unsubMutations: (() => void) | null = null;
  let unsubUploads: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let done = false;

  function cleanup() {
    done = true;
    if (unsubMutations) { unsubMutations(); unsubMutations = null; }
    if (unsubUploads) { unsubUploads(); unsubUploads = null; }
    if (timer !== null) { clearTimeout(timer); timer = null; }
  }

  function check() {
    if (done) return;
    if (countBlockingMutations(queryClient) === 0 && !hasActiveUploads()) {
      cleanup();
      resolve?.('settled');
    }
  }

  const promise = new Promise<HoldResult>((res) => {
    resolve = res;

    // Subscribe to mutation cache events.
    unsubMutations = queryClient.getMutationCache().subscribe(check);
    // Subscribe to upload-queue store changes.
    unsubUploads = useUploadQueueStore.subscribe(check);

    // Timeout ceiling.
    timer = setTimeout(() => {
      if (done) return;
      cleanup();
      onTimeout?.();
      res('timeout');
    }, maxMs);

    // Immediate check — the condition may already be met.
    check();
  });

  function cancel() {
    cleanup();
    // The promise never resolves — caller should not await after cancel.
  }

  return { promise, cancel };
}
